import { createRealtime } from 'pixel-realtime';
import { getBaseUrl, getUserToken } from '@src/API/helpers';
import { ls } from '@src/Services/Helpers/LocalStorageEnc';
import { store } from '@src/redux/store';

let client = null;

/**
 * Window event fired when a stored notification arrives over the socket, so the
 * navbar bell can refresh. The bell cannot subscribe to the channel itself:
 * `stop()` on a notifications subscription calls `echo.leave()`, which would
 * tear down the app-level listener's channel too.
 */
export const NEW_NOTIFICATION_EVENT = 'realtime:notification';

export const getCurrentUserId = () => store.getState().loginUser?.user?.id;

export const getRealtime = () => {
    if (client) return client;

    client = createRealtime({
        key:    process.env.REACT_APP_PIXEL_REALTIME_APP_KEY,
        host:   process.env.REACT_APP_PIXEL_REALTIME_HOST,
        port:   Number(process.env.REACT_APP_PIXEL_REALTIME_PORT),
        scheme: process.env.REACT_APP_PIXEL_REALTIME_SCHEME,
        authEndpoint: `${getBaseUrl()}/broadcasting/auth`,
        tokenProvider: getUserToken,
        // channel authorizers run the same view-as gate the HTTP routes do, and that gate reads
        // `view_as` off the request. pusher-js sends nothing but socket_id and the channel name,
        // so the value has to ride along here — resolved per auth request, because the client is
        // built once per page load while the active view-as changes as the user moves between tabs.
        auth: {
            paramsProvider: () => {
                const viewAs = ls.getItem('additionalParams')?.view_as;

                return viewAs ? { view_as: viewAs } : {};
            },
        },
        api: {
            baseUrl: `${getBaseUrl()}/pixel-realtime`,
            tokenProvider: getUserToken,
        },
        userId: getCurrentUserId(),
        // pusher-js routes TLS through the transport named 'ws' (useTLS:true), not 'wss';
        // the lib's forceTLS default of ["wss"] disables 'ws' → no transport → 'failed'.
        enabledTransports: ['ws', 'wss'],
    });

    return client;
};

// Connection status for the report's offline/reconnecting indicator.
// status: 'online' | 'reconnecting' | 'offline'. onChange fires immediately, then on every change.
export const onConnectionChange = (cb) => getRealtime().connection.onChange(cb);

// Force an immediate reconnect attempt (pusher-js already auto-reconnects; this is a manual nudge).
export const forceReconnect = () => getRealtime().connection.reconnect();

// The socket must not outlive the session: pusher-js keeps re-authenticating its
// channels after a logout, and /broadcasting/auth then answers 401 — which pusher-js
// itself throws on while building its HTTPAuthError. Resetting the client also drops
// the userId baked into it, so the next login gets its own channels.
// ponytail: reaches into pusher-js's own instance registry (window.Pusher is set by
// pixel-realtime) because the lib exposes no disconnect — drop it when it does.
export const disconnectRealtime = () => {
    window.Pusher?.instances?.forEach((pusher) => pusher.disconnect());
    client = null;
};
